import { Decimal } from '@paguz/contracts';
import { cn } from '@paguz/ui';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ErrorState, LoadingRows, Panel } from '../../components/page.tsx';
import { formatInt, formatRate } from '../../lib/format.ts';
import { AGENT_LABEL } from './labels.ts';

const usd = (value: string) => `USD ${new Decimal(value).toFixed(2)}`;

/**
 * Presupuesto LLM del día frente al tope global `LLM_DAILY_BUDGET_USD` (al llegar al tope los
 * agentes pausan y ofrecen contacto humano). Lo usan Agentes, Configuración › IA y el Dashboard.
 */
export function UsagePanel({ compact = false }: { compact?: boolean }) {
  const query = useQuery({
    queryKey: queryKeys.agentUsage,
    queryFn: api.getAgentUsage,
    refetchInterval: 30_000,
  });
  if (query.isPending)
    return (
      <Panel title="Presupuesto LLM del día">
        <LoadingRows rows={3} />
      </Panel>
    );
  if (query.isError)
    return (
      <Panel title="Presupuesto LLM del día">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Panel>
    );
  const u = query.data;
  const ratio = Decimal.min(new Decimal(u.spentUsd).div(u.budgetUsd), 1);
  const pct = ratio.times(100).toNumber();
  const tone = ratio.gte(1) ? 'bg-destructive' : ratio.gte(0.8) ? 'bg-warning' : 'bg-highlight';
  return (
    <Panel
      title="Presupuesto LLM del día"
      description={`Tope global LLM_DAILY_BUDGET_USD = ${usd(u.budgetUsd)} · por cliente ${usd(u.userBudgetUsd)} al día`}
    >
      <div className="flex flex-col gap-4">
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-display text-2xl font-semibold text-foreground">{usd(u.spentUsd)}</p>
            <p className="text-sm text-muted-foreground">
              {formatRate(ratio.toFixed(4), '', 0)} de {usd(u.budgetUsd)}
            </p>
          </div>
          <div
            role="progressbar"
            aria-label="Consumo del presupuesto LLM del día"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
            className="mt-2 h-2.5 overflow-hidden rounded-full bg-muted"
          >
            <div className={cn('h-full rounded-full', tone)} style={{ width: `${pct}%` }} />
          </div>
          {ratio.gte(0.8) && (
            <p className="mt-2 text-sm text-warning-soft-foreground">
              Al llegar al tope los agentes pausan y ofrecen contacto humano.
            </p>
          )}
        </div>
        <dl className={cn('grid grid-cols-2 gap-3 text-sm', !compact && 'xl:grid-cols-4')}>
          <Metric label="Tokens de entrada" value={formatInt(u.tokens.input)} />
          <Metric label="Tokens de salida" value={formatInt(u.tokens.output)} />
          <Metric label="Leídos de caché" value={formatInt(u.tokens.cacheRead)} />
          <Metric label="Cache hit" value={formatRate(u.cacheHitRate, '', 0)} />
        </dl>
        {!compact && (
          <>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Metric
                label="Costo por expediente"
                value={u.costPerApplicationUsd ? usd(u.costPerApplicationUsd) : '—'}
              />
              <Metric
                label="Costo por compra conversacional"
                value={u.costPerPurchaseUsd ? usd(u.costPerPurchaseUsd) : '—'}
              />
            </dl>
            <div>
              <p className="mb-1.5 text-xs font-semibold text-muted-foreground">
                Por rol (sesiones de la muestra)
              </p>
              <ul className="flex flex-col gap-1 text-sm">
                {u.byRole.map((r) => (
                  <li key={r.role} className="flex justify-between gap-3">
                    <span>{AGENT_LABEL[r.role]}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {r.calls} llamadas · USD {r.costUsd}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted/60 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
